import {ChevronDownIcon, ChevronRightIcon, XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {HIGHLIGHT_SUBSET, useLowlight} from '../shared/use-lowlight'
import {highlightCodeToNodes} from '../shared/hast-helpers'
import hljsStyles from '../shared/hljs-tokens.module.css'
import styles from './PrettifiedCodeBlock.module.css'

interface PrettifiedCodeBlockProps {
  code: string
  collapsed: boolean
  onRemove: () => void
  onToggleCollapse: () => void
  onCodeChange: (code: string) => void
  readOnly?: boolean
}

function typingDelay(codeLength: number): number {
  if (codeLength > 8_500) return 750
  return 200
}

// small optimization; large blocks cause lag when typing
function useIsTyping(code: string): [boolean, () => void] {
  const [isTyping, setIsTyping] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onType = useCallback(() => {
    setIsTyping(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    const delay = typingDelay(code.length)
    timerRef.current = setTimeout(() => setIsTyping(false), delay)
  }, [code.length])

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  return [isTyping, onType]
}

export function PrettifiedCodeBlock({
  code,
  collapsed,
  onRemove,
  onToggleCollapse,
  onCodeChange,
  readOnly = false,
}: PrettifiedCodeBlockProps) {
  const label = 'Pasted content'
  const lowlight = useLowlight()
  const [initialCode] = useState(code)
  const [isTyping, onType] = useIsTyping(code)

  const detectedLanguage = useMemo(() => {
    if (!lowlight) return null
    try {
      return lowlight.highlightAuto(initialCode, {subset: HIGHLIGHT_SUBSET}).data?.language ?? null
    } catch {
      return null
    }
  }, [lowlight, initialCode])

  const highlightedNodes = useMemo(() => {
    if (!lowlight || collapsed || isTyping) return null
    return highlightCodeToNodes(lowlight, code, detectedLanguage)
  }, [lowlight, code, collapsed, isTyping, detectedLanguage])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value
      if (!val) return onRemove()
      onCodeChange(val)
      onType()
    },
    [onCodeChange, onRemove, onType],
  )

  return (
    <div className={clsx(styles.container, collapsed && styles.collapsed)} data-testid="prettified-code-block">
      <div className={styles.header}>
        <IconButton
          size="small"
          icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
          aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
          aria-expanded={!collapsed}
          onClick={onToggleCollapse}
          variant="invisible"
          tooltipDirection="n"
          className={styles.headerButton}
        />
        <span className={styles.languageLabel}>{label}</span>
        {!readOnly ? (
          <IconButton
            size="small"
            icon={XIcon}
            aria-label={`Remove ${label}`}
            onClick={onRemove}
            variant="invisible"
            tooltipDirection="n"
            className={styles.headerButton}
          />
        ) : null}
      </div>
      {!collapsed && (
        <div className={styles.codeWrapper}>
          {highlightedNodes && (
            <pre className={clsx(styles.codeHighlight, hljsStyles.tokens)} aria-hidden>
              <code>{highlightedNodes}</code>
            </pre>
          )}
          <textarea
            className={clsx(styles.codeTextarea, highlightedNodes && styles.codeTextareaHighlighted)}
            value={code}
            onChange={readOnly ? undefined : handleChange}
            aria-label={label}
            aria-readonly={readOnly}
            readOnly={readOnly}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </div>
      )}
    </div>
  )
}
